// goal_editor_view.js — pure HTML-generating module for the full-space
// goal editor in Settings → Goals. No DOM access at import time so it
// can be tested under Node.js with `node --test`.

/**
 * Escape HTML special characters to prevent XSS.
 * @param {*} v - value to escape
 * @returns {string} HTML-safe string
 */
export function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * Generate HTML for a single reference row.
 * @param {object} r - reference object { id, chip, title, url }
 * @returns {string} HTML string
 */
export function refRowHtml(r) {
  return `
    <div class="goal-editor__ref-row" data-row="${esc(r.id)}">
      <div class="goal-editor__ref-fields">
        <input class="goal-editor__input" data-c="title" value="${esc(r.title)}" placeholder="Title — e.g. DDS Social Rec Directive §4688.22">
        <div class="goal-editor__ref-row-sub">
          <input class="goal-editor__input goal-editor__input--chip" data-c="chip" value="${esc(r.chip)}" placeholder="Short label (optional)">
          <input class="goal-editor__input goal-editor__input--url" data-c="url" value="${esc(r.url)}" placeholder="https://… (optional)">
        </div>
      </div>
      <button type="button" class="goal-editor__del-btn" data-del="refs" title="Remove">×</button>
    </div>`
}

/**
 * Generate HTML for a single vendor row (linked to vendor library).
 * Displays vendor name and contact info pulled live from the library.
 * @param {object} v - resolved vendor object { id, name, phone, email, ... }
 * @returns {string} HTML string
 */
export function vendorRowHtml(v) {
  const name = v.name || "(unnamed)"
  // Build meta string from phone/email
  const meta = [v.phone, v.email].filter(Boolean).join(" · ")
  return `
    <div class="goal-editor__vendor-row" data-row="${esc(v.id)}">
      <div class="goal-editor__vendor-info">
        <span class="goal-editor__vendor-name">${esc(name)}</span>
        ${meta ? `<span class="goal-editor__vendor-meta">${esc(meta)}</span>` : ""}
      </div>
      <button type="button" class="goal-editor__del-btn" data-del="vendors" title="Remove">×</button>
    </div>`
}

/**
 * Generate HTML for the vendor picker add row.
 * This renders a container for the vendor-picker Stimulus controller.
 * @returns {string} HTML string
 */
export function vendorPickerRowHtml() {
  return `
    <div class="goal-editor__vendor-picker-row" data-controller="vendor-picker" data-vendor-picker-value-value="" data-vendor-picker-name-value="" data-vendor-picker-placeholder-value="Search or add a vendor...">
    </div>`
}

/**
 * Build the full-space goal editor HTML (Settings → Goals variant).
 *
 * @param {object} opts
 * @param {object} opts.goal - goal object { name, category, domain, timeline, description }
 * @param {string|null} opts.serial - existing serial (e.g. "PCP 012.01") or null for new
 * @param {object} opts.categories - { key: label } map of category options
 * @param {Array} opts.refs - array of reference objects
 * @param {Array} opts.vendors - array of resolved vendor objects (from vendor library)
 * @returns {string} full editor HTML string
 */
export function goalEditorHtml({ goal, serial, categories, refs, vendors }) {
  const isNew = !serial
  const heading = isNew ? "Add a goal" : `Edit ${esc(serial)}`

  const categoryOptions = Object.entries(categories)
    .map(([k, label]) => `<option value="${esc(k)}"${goal.category === k ? " selected" : ""}>${esc(label)}</option>`)
    .join("")

  const refsHtml = refs.length
    ? refs.map(refRowHtml).join("")
    : `<div class="goal-editor__empty">None yet.</div>`

  const vendorsHtml = vendors.length
    ? vendors.map(vendorRowHtml).join("")
    : ""

  const famLabel = isNew ? "this goal" : `PCP ${String(serial.split(" ")[1]?.split(".")[0] || "").padStart(3, "0")}`

  return `
<div class="goal-editor">
  <div class="goal-editor__header">
    <h3 class="goal-editor__title">${heading}</h3>
    <p class="goal-editor__subtitle">Saved to your local goal library.</p>
  </div>
  <div class="goal-editor__body">
    <div class="goal-editor__main">
      <label class="goal-editor__label">Name</label>
      <input class="goal-editor__input" data-f="name" value="${esc(goal.name)}">

      <label class="goal-editor__label">Category</label>
      <select class="goal-editor__input" data-f="category">${categoryOptions}</select>

      <label class="goal-editor__label">Domain</label>
      <input class="goal-editor__input" data-f="domain" value="${esc(goal.domain)}">

      <label class="goal-editor__label">Timeline</label>
      <input class="goal-editor__input" data-f="timeline" value="${esc(goal.timeline)}">

      <label class="goal-editor__label">Description</label>
      <textarea class="goal-editor__input goal-editor__input--textarea" data-f="description" rows="10">${esc(goal.description)}</textarea>
    </div>
    <div class="goal-editor__aside">
      <label class="goal-editor__label">References <span class="goal-editor__label-note">— citations for ${esc(famLabel)}, shared across all its variations</span></label>
      <div data-list="refs">${refsHtml}</div>
      <button type="button" class="goal-editor__add-btn goal-editor__add-btn--ref" data-add="refs">＋ Add reference</button>

      <label class="goal-editor__label goal-editor__label--vendors">Vendors <span class="goal-editor__label-note">— candidate providers to choose from when using this goal</span></label>
      <div data-list="vendors">${vendorsHtml}</div>
      ${vendorPickerRowHtml()}
    </div>
  </div>
  <div class="goal-editor__actions">
    <button type="button" class="goal-editor__cancel-btn" data-role="cancel">Cancel</button>
    <button type="button" class="goal-editor__save-btn" data-role="save">${isNew ? "Add goal" : "Save"}</button>
  </div>
</div>`
}

/**
 * Build group options HTML for the wizard goal editor.
 * Generates <option> elements for all known groups, plus an "Other" fallback
 * that is selected when the goal's group is unknown.
 *
 * @param {object} groups - { key: label } map of group options
 * @param {string} selectedGroup - the currently selected group key
 * @returns {string} HTML string of <option> elements
 */
export function groupOptionsHtml(groups, selectedGroup) {
  const knownGroup = selectedGroup && (selectedGroup in groups)
  return Object.entries(groups)
    .map(([k, label]) => `<option value="${esc(k)}"${selectedGroup === k ? " selected" : ""}>${esc(label)}</option>`)
    .join("") + `<option value="other"${knownGroup ? "" : " selected"}>Other</option>`
}

/**
 * Build the full-space goal editor HTML (wizard "sheet" variant).
 * This is a full-viewport overlay for editing goal suggestions in the wizard.
 *
 * @param {object} opts
 * @param {object} opts.goal - goal object { heading, domain, timeline, frequency, description, group }
 * @param {boolean} opts.isNew - true if adding a new goal
 * @param {boolean} opts.isCustom - true if goal is custom (show "Delete"), false if built-in (show "Reset to default")
 * @param {object} opts.groups - { key: label } map of group options
 * @param {string} opts.subtitle - subtitle text (e.g. "Saved to your library (synced via OneDrive)...")
 * @param {string} opts.variant - "sheet" for full-viewport overlay
 * @returns {string} full editor HTML string
 */
export function wizardGoalEditorHtml({ goal, isNew, isCustom, groups, subtitle, variant }) {
  const heading = isNew ? "Add your own goal" : "Edit suggested goal"
  const rootClass = variant === "sheet" ? "goal-editor goal-editor--sheet" : "goal-editor"

  const groupOptions = groupOptionsHtml(groups, goal.group)

  // Third button: absent for new, "Delete" for custom, "Reset to default" otherwise
  let thirdBtnHtml = ""
  if (!isNew) {
    const label = isCustom ? "Delete" : "Reset to default"
    thirdBtnHtml = `<button type="button" class="goal-editor__reset-btn" data-role="reset">${esc(label)}</button>`
  }

  return `
<div class="${rootClass}">
  <div class="goal-editor__header">
    <h3 class="goal-editor__title">${esc(heading)}</h3>
    <p class="goal-editor__subtitle">${esc(subtitle)}</p>
  </div>
  <div class="goal-editor__body">
    <div class="goal-editor__main">
      <label class="goal-editor__label">Category</label>
      <select class="goal-editor__input" data-f="group">${groupOptions}</select>

      <label class="goal-editor__label">Heading</label>
      <input class="goal-editor__input" data-f="heading" value="${esc(goal.heading)}">

      <label class="goal-editor__label">Domain</label>
      <input class="goal-editor__input" data-f="domain" value="${esc(goal.domain)}">

      <label class="goal-editor__label">Timeline</label>
      <input class="goal-editor__input" data-f="timeline" value="${esc(goal.timeline)}">

      <label class="goal-editor__label">Frequency</label>
      <input class="goal-editor__input" data-f="frequency" value="${esc(goal.frequency)}">
    </div>
    <div class="goal-editor__aside">
      <label class="goal-editor__label">Description</label>
      <textarea class="goal-editor__input goal-editor__input--textarea" data-f="description" rows="10">${esc(goal.description)}</textarea>
    </div>
  </div>
  <div class="goal-editor__actions goal-editor__actions--spread">
    ${thirdBtnHtml || '<span></span>'}
    <div class="goal-editor__actions-right">
      <button type="button" class="goal-editor__cancel-btn" data-role="cancel">Cancel</button>
      <button type="button" class="goal-editor__save-btn" data-role="save">${isNew ? "Add goal" : "Save"}</button>
    </div>
  </div>
</div>`
}
